import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  ChevronDown,
  Eraser,
  FileDown,
  Plus,
  Calendar,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { getAllDepartments } from "../../../api/hrm/departments/departmentInfo";
import { hasPermission } from "../../../utils/permissionUtils";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import FinancePeriodFilterControls from "../shared/FinancePeriodFilterControls";
import {
  applyPeriodChange,
  currentPeriodDefaults,
} from "../../reports/finance/financePeriodUtils";

const buildInitialFilters = () => {
  const period = currentPeriodDefaults();
  return {
    keyword: "",
    branch_id: "",
    department_id: "",
    status: "",
    period_type: period.period_type,
    year: period.year,
    quarter: period.quarter,
    month: period.month,
    from_date: period.from_date,
    to_date: period.to_date,
  };
};

const AdvanceFilter = ({
  onFilterChange,
  onNewClick,
  onRefresh,
  status: statusProp,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const menuRef = useRef(null);

  const [filters, setFilters] = useState(buildInitialFilters);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    getAllBranches()
      .then((res) => {
        const items = res.items || res || [];
        setBranchOptions(items.map((b) => ({ id: b.id, name: b.name })));
      })
      .catch(() => setBranchOptions([]));

    getAllDepartments()
      .then((res) => {
        const items = res.items || res || [];
        setDeptOptions(items.map((d) => ({ id: d.id, name: d.name })));
      })
      .catch(() => setDeptOptions([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => onFilterChange?.(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (statusProp === undefined) return;
    setFilters((prev) =>
      prev.status === statusProp ? prev : { ...prev, status: statusProp || "" }
    );
  }, [statusProp]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowTemplateMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const setPeriod = (patch) => {
    setFilters((prev) => applyPeriodChange(prev, patch));
  };

  const clearFilters = () => {
    const cleared = buildInitialFilters();
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const isFiltering =
    filters.branch_id !== "" ||
    filters.department_id !== "" ||
    filters.status !== "" ||
    (filters.keyword && filters.keyword.trim() !== "");

  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 shrink-0 flex items-center justify-center rounded-xl border-2 transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-300 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange, placeholder }) => (
    <div className="flex flex-col gap-1.5 w-full text-left">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.name}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  const STATUS_OPTIONS = [
    { id: "draft", name: "Nháp" },
    { id: "waiting", name: "Chờ duyệt" },
    { id: "approved", name: "Đã duyệt" },
    { id: "rejected", name: "Từ chối" },
    { id: "completed", name: "Đã quyết toán" },
  ];

  const AdvancedFields = () => (
    <>
      <SelectBox
        label="Chi nhánh"
        value={filters.branch_id}
        options={branchOptions}
        onChange={(v) => updateFilter("branch_id", v)}
        placeholder="Tất cả chi nhánh"
      />
      <SelectBox
        label="Bộ phận"
        value={filters.department_id}
        options={deptOptions}
        onChange={(v) => updateFilter("department_id", v)}
        placeholder="Tất cả bộ phận"
      />
      <SelectBox
        label="Trạng thái"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => updateFilter("status", v)}
        placeholder="Tất cả trạng thái"
      />
      <div className="flex flex-col gap-1.5 w-full">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
          Từ ngày
        </label>
        <div className="relative group">
          <Calendar
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="date"
            value={filters.from_date}
            onChange={(e) => updateFilter("from_date", e.target.value)}
            className="w-full bg-white border border-gray-200 py-3 pl-11 pr-4 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5 w-full">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
          Đến ngày
        </label>
        <div className="relative group">
          <Calendar
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="date"
            value={filters.to_date}
            onChange={(e) => updateFilter("to_date", e.target.value)}
            className="w-full bg-white border border-gray-200 py-3 pl-11 pr-4 rounded-2xl text-sm font-medium outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
          />
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <div className="mb-4 space-y-2.5">
        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm p-3 space-y-3.5">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 px-0.5">
              Thời gian
            </p>
            <FinancePeriodFilterControls
              periodType={filters.period_type}
              year={filters.year}
              quarter={filters.quarter}
              month={filters.month}
              onPeriodChange={setPeriod}
              compact
              strongBorder
              className="w-full"
            />
          </div>

          <div className="pt-3 border-t border-gray-100">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 px-0.5">
              Tìm kiếm
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 group min-w-0">
                <Search
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Mã phiếu, nhân viên..."
                  value={filters.keyword}
                  onChange={(e) => updateFilter("keyword", e.target.value)}
                  className="w-full h-11 bg-gray-50 border-2 border-gray-200 pl-10 pr-3 rounded-xl focus:outline-none focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all text-sm font-medium"
                />
              </div>
              <ActionButton
                icon={SlidersHorizontal}
                onClick={() => setAdvanced(!advanced)}
                active={advanced || isFiltering}
                title="Bộ lọc"
              />
              <ActionButton
                icon={RotateCw}
                onClick={() =>
                  onRefresh ? onRefresh() : onFilterChange?.(filters)
                }
                title="Làm mới"
              />
            </div>
          </div>
        </div>

        <p className="text-[11px] text-gray-400 font-medium px-0.5">
          Trên điện thoại chỉ xem đề nghị tạm ứng. Tạo / sửa thực hiện trên máy
          tính.
        </p>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc tạm ứng"
          footer={
            <>
              <button
                type="button"
                onClick={clearFilters}
                className="flex-1 h-11 rounded-xl border border-gray-200 text-gray-600 font-bold flex items-center justify-center gap-2"
              >
                <Eraser size={16} /> Xóa lọc
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 h-11 rounded-xl bg-red-600 text-white font-bold"
              >
                Áp dụng
              </button>
            </>
          }
        >
          <div className="space-y-4">
            <AdvancedFields />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="bg-white p-3 sm:p-5 rounded-2xl border border-gray-100 shadow-sm mb-4 md:mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <FinancePeriodFilterControls
            periodType={filters.period_type}
            year={filters.year}
            quarter={filters.quarter}
            month={filters.month}
            onPeriodChange={setPeriod}
            compact
            className="shrink-0"
          />

          <div className="relative flex-1 min-w-[180px] sm:min-w-[220px] max-w-md group">
            <Search
              className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm mã phiếu, nhân viên..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full h-11 bg-white border border-gray-200 pl-10 sm:pl-11 pr-3 sm:pr-4 rounded-xl outline-none transition-all font-medium text-sm text-gray-700 focus:border-red-500 focus:ring-4 focus:ring-red-500/5"
            />
          </div>

          <ActionButton
            icon={RotateCw}
            onClick={() =>
              onRefresh ? onRefresh() : onFilterChange?.(filters)
            }
            title="Làm mới"
          />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title="Lọc nâng cao"
          />
          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="hidden sm:flex text-xs font-bold text-red-500 hover:text-red-700 items-center gap-1 uppercase tracking-wider transition-all ml-1"
            >
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        <div className="hidden md:flex items-center gap-2 shrink-0" ref={menuRef}>
          {hasPermission("finance.advance.access", "R") && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                className="h-11 px-5 flex items-center gap-2 rounded-2xl border border-gray-200 text-gray-600 font-bold text-xs uppercase tracking-wider hover:border-red-300 hover:text-red-600 transition-all bg-white"
              >
                <FileDown size={16} />
                Biểu mẫu
                <ChevronDown
                  size={14}
                  className={`transition-transform ${
                    showTemplateMenu ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence>
                {showTemplateMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-2xl shadow-xl py-2 z-50"
                  >
                    <a
                      href={`${window.location.origin}/templates/hrm/advance/frm_advance_salary.docx`}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      Tạm ứng lương
                    </a>
                    <a
                      href={`${window.location.origin}/templates/hrm/advance/frm_advance_purchase.docx`}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      Tạm ứng mua hàng
                    </a>
                    <a
                      href={`${window.location.origin}/templates/hrm/advance/frm_advance_other.docx`}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      Tạm ứng khác
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {hasPermission("finance.advance.create", "C") && (
            <button
              type="button"
              onClick={onNewClick}
              className="h-11 px-6 flex items-center gap-2 bg-red-700 text-white rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg shadow-red-100 hover:bg-red-800 transition-all active:scale-95"
            >
              <Plus size={18} />
              Tạo đề nghị
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mt-6 pt-6 border-t border-gray-100">
              <AdvancedFields />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdvanceFilter;
